/**
 * /contact page i18n catalog.
 *
 * Source: specs/GTM/GNPC-APPLICATION-KIT.md §3.2 (VN authoritative draft).
 * GNPC requires a public contact page with takedown SLA + DSAR channel
 * for editorial accountability.
 */

import type { Lang } from '../use-lang';

export interface ContactRow {
  purpose: string;
  email: string;
  sla: string;
}

export interface ContactCopy {
  meta_title: string;
  meta_description: string;
  page_title: string;

  channels_heading: string;
  channels_intro: string;
  table_purpose: string;
  table_email: string;
  table_sla: string;
  rows: ContactRow[];

  takedown_heading: string;
  takedown_intro: string;
  takedown_step_1: string;
  takedown_step_2: string;
  takedown_step_3: string;
  takedown_form_label: string;

  dsar_heading: string;
  dsar_intro: string;
  dsar_right_1: string;
  dsar_right_2: string;
  dsar_right_3: string;
  dsar_right_4: string;
  dsar_send_to: string;
  dsar_manage_link: string;
}

export const CONTACT_VI: ContactCopy = {
  meta_title: 'Liên hệ Asklaw',
  meta_description:
    'Kênh liên hệ Asklaw: email chung, DPO bảo vệ dữ liệu, pháp lý + takedown, báo lỗi. SLA 48 giờ cho takedown.',
  page_title: 'Liên hệ Asklaw',

  channels_heading: 'Email kênh chính',
  channels_intro:
    'Mỗi mục đích có một địa chỉ email riêng để giúp Asklaw phản hồi nhanh và đúng người phụ trách.',
  table_purpose: 'Mục đích',
  table_email: 'Email',
  table_sla: 'SLA',
  rows: [
    {
      purpose: 'Phản hồi chung',
      email: 'hello@asklaw.vn',
      sla: '7 ngày làm việc',
    },
    {
      purpose: 'Bảo vệ dữ liệu (DPO)',
      email: 'dpo@asklaw.vn',
      sla: '30 ngày (Luật PDP 2026 Điều 14)',
    },
    {
      purpose: 'Pháp lý + takedown',
      email: 'legal@asklaw.vn',
      sla: '48 giờ (takedown SLA)',
    },
    {
      purpose: 'Báo lỗi',
      email: 'bug@asklaw.vn',
      sla: '7 ngày',
    },
    {
      purpose: 'Đối tác',
      email: 'hello@asklaw.vn',
      sla: '14 ngày',
    },
  ],

  takedown_heading: 'Yêu cầu gỡ tin',
  takedown_intro:
    'Nếu bạn là chủ sở hữu nội dung được trích dẫn trên Asklaw Hot News và muốn gỡ:',
  takedown_step_1:
    'Gửi email đến legal@asklaw.vn với chủ đề "Takedown — [URL]"',
  takedown_step_2: 'Hoặc dùng biểu mẫu yêu cầu gỡ tin',
  takedown_step_3:
    'Asklaw cam kết phản hồi trong 48 giờ và xử lý theo Luật Sở hữu trí tuệ 2005.',
  takedown_form_label: 'Mở biểu mẫu gỡ tin',

  dsar_heading: 'Quyền của chủ thể dữ liệu (DSAR)',
  dsar_intro:
    'Theo Luật PDP 2026 Điều 14, bạn có quyền:',
  dsar_right_1: 'Yêu cầu truy cập dữ liệu cá nhân Asklaw đang lưu (export)',
  dsar_right_2: 'Yêu cầu xoá dữ liệu (right to erasure)',
  dsar_right_3: 'Yêu cầu sửa đổi dữ liệu sai',
  dsar_right_4: 'Rút lại đồng ý nhận bản tin (one-click qua link trong email)',
  dsar_send_to: 'Gửi yêu cầu đến dpo@asklaw.vn hoặc trang quản lý subscriber.',
  dsar_manage_link: 'Quản lý đăng ký bản tin',
};

export const CONTACT_EN: ContactCopy = {
  meta_title: 'Contact Asklaw',
  meta_description:
    'Asklaw contact channels: general email, DPO data protection, legal + takedown, bug report. 48-hour takedown SLA.',
  page_title: 'Contact Asklaw',

  channels_heading: 'Main email channels',
  channels_intro:
    'Each purpose has its own email so Asklaw can route requests to the right person quickly.',
  table_purpose: 'Purpose',
  table_email: 'Email',
  table_sla: 'SLA',
  rows: [
    {
      purpose: 'General feedback',
      email: 'hello@asklaw.vn',
      sla: '7 business days',
    },
    {
      purpose: 'Data Protection (DPO)',
      email: 'dpo@asklaw.vn',
      sla: '30 days (PDP Law 2026 Art. 14)',
    },
    {
      purpose: 'Legal + takedown',
      email: 'legal@asklaw.vn',
      sla: '48 hours (takedown SLA)',
    },
    {
      purpose: 'Bug report',
      email: 'bug@asklaw.vn',
      sla: '7 days',
    },
    {
      purpose: 'Partnerships',
      email: 'hello@asklaw.vn',
      sla: '14 days',
    },
  ],

  takedown_heading: 'Takedown requests',
  takedown_intro:
    'If you own content cited on Asklaw Hot News and want it removed:',
  takedown_step_1:
    'Email legal@asklaw.vn with subject "Takedown — [URL]"',
  takedown_step_2: 'Or use the takedown form',
  takedown_step_3:
    'Asklaw commits to respond within 48 hours and process under IP Law 2005.',
  takedown_form_label: 'Open takedown form',

  dsar_heading: 'Data subject rights (DSAR)',
  dsar_intro: 'Under PDP Law 2026 Article 14, you have the right to:',
  dsar_right_1:
    'Request access to personal data Asklaw stores about you (export)',
  dsar_right_2: 'Request erasure (right to be forgotten)',
  dsar_right_3: 'Request correction of inaccurate data',
  dsar_right_4:
    'Withdraw consent to the news brief (one-click via the email footer link)',
  dsar_send_to:
    'Send requests to dpo@asklaw.vn or use the subscriber management page.',
  dsar_manage_link: 'Manage news brief subscription',
};

export function resolveContact(lang: Lang): ContactCopy {
  return lang === 'en' ? CONTACT_EN : CONTACT_VI;
}
